import { calculate_combo_price } from './calculate_combo_price';

const to_number = (value) => {
    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
        return parsedValue;
    }

    return 0;
};

const clamp_discount = (value) => {
    const parsedValue = to_number(value);

    if (parsedValue > 100) {
        return 100;
    }

    return parsedValue;
};

const round_money = (value) => {
    return Math.round((to_number(value) + Number.EPSILON) * 100) / 100;
};

const service_net_price = (serviceItem, vatEnabled, vatRateValue) => {
    const servicePrice = to_number(serviceItem.servicePriceNumber);
    const serviceVatIncluded = vatEnabled && Boolean(serviceItem.serviceVatIncluded) && vatRateValue > 0;

    if (!serviceVatIncluded) {
        return servicePrice;
    }

    return servicePrice / (1 + vatRateValue);
};

export const calculate_offer_totals = (params = {}) => {
    const pricedServices = Array.isArray(params?.pricedServices) ? params.pricedServices : [];
    const typeDiscountValues = params?.typeDiscountValues || {};
    const vatEnabled = params?.vatEnabled ?? true;
    const vatRateValue = vatEnabled ? to_number(params?.vatRate) : 0;
    const oneOffServicesAreFree = pricedServices.some((serviceItem) => !serviceItem.serviceOneOff)
        && pricedServices.some((serviceItem) => serviceItem.serviceOneOff);
    const servicesByType = pricedServices.reduce((typeLookup, serviceItem) => {
        const serviceType = serviceItem?.serviceType || '';
        const currentTypeItems = typeLookup[serviceType] || [];

        return {
            ...typeLookup,
            [serviceType]: [...currentTypeItems, serviceItem]
        };
    }, {});
    const typeEntries = Object.entries(servicesByType);
    const typeTotals = {};
    const allocatedServices = [];

    typeEntries.forEach(([typeName, typeServices]) => {
        const standaloneSubtotal = typeServices.reduce((sum, serviceItem) => {
            return sum + service_net_price(serviceItem, vatEnabled, vatRateValue);
        }, 0);
        const billableTypeServices = oneOffServicesAreFree
            ? typeServices.filter((serviceItem) => !serviceItem.serviceOneOff)
            : typeServices;
        const freeOneOffSubtotal = oneOffServicesAreFree
            ? typeServices
                .filter((serviceItem) => serviceItem.serviceOneOff)
                .reduce((sum, serviceItem) => sum + service_net_price(serviceItem, vatEnabled, vatRateValue), 0)
            : 0;
        const billableStandaloneSubtotal = billableTypeServices.reduce((sum, serviceItem) => {
            return sum + service_net_price(serviceItem, vatEnabled, vatRateValue);
        }, 0);
        const typeServiceNames = billableTypeServices.map((serviceItem) => serviceItem.serviceName);
        const typeServicePrices = billableTypeServices.reduce((servicePriceLookup, serviceItem) => {
            return {
                ...servicePriceLookup,
                [serviceItem.serviceName]: service_net_price(serviceItem, vatEnabled, vatRateValue)
            };
        }, {});
        const comboTotals = typeName === 'marketing' && billableTypeServices.length
            ? calculate_combo_price(typeServiceNames, typeServicePrices)
            : null;
        const subtotalBeforeDiscount = comboTotals ? to_number(comboTotals.total) : billableStandaloneSubtotal;
        const discountPercent = clamp_discount(typeDiscountValues[typeName]);
        const discountAmount = subtotalBeforeDiscount * (discountPercent / 100);
        const totalAfterDiscount = subtotalBeforeDiscount - discountAmount;
        const allocationRatio = billableStandaloneSubtotal > 0 ? totalAfterDiscount / billableStandaloneSubtotal : 0;
        const typeAllocatedServices = typeServices.map((serviceItem) => {
            const standalonePrice = service_net_price(serviceItem, vatEnabled, vatRateValue);
            const serviceIsFreeOneOff = oneOffServicesAreFree && serviceItem.serviceOneOff;

            if (serviceIsFreeOneOff) {
                return {
                    ...serviceItem,
                    servicePriceNumber: standalonePrice,
                    netAfterDiscount: 0,
                    discountAmount: standalonePrice,
                    oneOffFreeWithRecurring: true
                };
            }

            const netAfterDiscount = standalonePrice * allocationRatio;

            return {
                ...serviceItem,
                servicePriceNumber: standalonePrice,
                netAfterDiscount,
                discountAmount: standalonePrice - netAfterDiscount
            };
        });

        typeTotals[typeName] = {
            standaloneSubtotal,
            subtotalBeforeDiscount: subtotalBeforeDiscount + freeOneOffSubtotal,
            billableSubtotalBeforeDiscount: subtotalBeforeDiscount,
            freeOneOffSubtotal,
            comboDiscount: comboTotals ? to_number(comboTotals.comboDiscount) : 0,
            discountPercent,
            discountAmount: standaloneSubtotal - totalAfterDiscount,
            totalAfterDiscount
        };

        allocatedServices.push(...typeAllocatedServices);
    });

    const subtotalBeforeDiscount = Object.values(typeTotals).reduce((sum, typeTotal) => {
        return sum + to_number(typeTotal.subtotalBeforeDiscount);
    }, 0);
    const totalDiscountAmount = Object.values(typeTotals).reduce((sum, typeTotal) => {
        return sum + to_number(typeTotal.discountAmount);
    }, 0);
    const recurringNetAmount = allocatedServices
        .filter((serviceItem) => !serviceItem.serviceOneOff)
        .reduce((sum, serviceItem) => sum + to_number(serviceItem.netAfterDiscount), 0);
    const oneOffNetAmount = allocatedServices
        .filter((serviceItem) => serviceItem.serviceOneOff)
        .reduce((sum, serviceItem) => sum + to_number(serviceItem.netAfterDiscount), 0);
    const totalNetAmount = recurringNetAmount + oneOffNetAmount;
    const recurringVatAmount = recurringNetAmount * vatRateValue;
    const oneOffVatAmount = oneOffNetAmount * vatRateValue;
    const totalVatAmount = recurringVatAmount + oneOffVatAmount;
    const recurringTotalWithVat = recurringNetAmount + recurringVatAmount;
    const oneOffTotalWithVat = oneOffNetAmount + oneOffVatAmount;
    const totalWithVat = recurringTotalWithVat + oneOffTotalWithVat;
    const hasRecurringServices = allocatedServices.some((serviceItem) => !serviceItem.serviceOneOff);
    const hasOneOffServices = allocatedServices.some((serviceItem) => serviceItem.serviceOneOff);
    const recurringSubtotalBeforeDiscount = allocatedServices
        .filter((serviceItem) => !serviceItem.serviceOneOff)
        .reduce((sum, serviceItem) => sum + to_number(serviceItem.servicePriceNumber), 0);
    const oneOffSubtotalBeforeDiscount = allocatedServices
        .filter((serviceItem) => serviceItem.serviceOneOff)
        .reduce((sum, serviceItem) => sum + to_number(serviceItem.servicePriceNumber), 0);

    return {
        totalsByType: typeTotals,
        allocatedServices,
        subtotalBeforeDiscount: round_money(subtotalBeforeDiscount),
        totalDiscountAmount: round_money(totalDiscountAmount),
        totalNetAmount: round_money(totalNetAmount),
        recurringSubtotalBeforeDiscount: round_money(recurringSubtotalBeforeDiscount),
        oneOffSubtotalBeforeDiscount: round_money(oneOffSubtotalBeforeDiscount),
        recurringNetAmount: round_money(recurringNetAmount),
        oneOffNetAmount: round_money(oneOffNetAmount),
        totalVatAmount: round_money(totalVatAmount),
        recurringVatAmount: round_money(recurringVatAmount),
        oneOffVatAmount: round_money(oneOffVatAmount),
        recurringTotalWithVat: round_money(recurringTotalWithVat),
        oneOffTotalWithVat: round_money(oneOffTotalWithVat),
        totalWithVat: round_money(totalWithVat),
        vatRateValue,
        hasRecurringServices,
        hasOneOffServices
    };
};
